/* hs-eslint ignored failing-rules */
/* eslint-disable no-prototype-builtins */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["actionButtons", "loadingIndicatorProps", "preload", "progressIndicatorProps", "showLoadingIndicator", "showProgressIndicator", "title"],
  _excluded2 = ["data-test-id", "iframePassthruProps"];
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { OptimisticNanoProgress } from 'foundations-components/transitional/components/OptimisticNanoProgress';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { withTheme } from 'foundations-theming/utils';
import { Component, Fragment, createRef, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import emptyFunction from 'react-utils/emptyFunction';
import styled from 'styled-components';
import { MSG_TYPE_MODAL_DIALOG_CLOSE } from 'ui-addon-iframeable/messaging/IFrameMessageTypes';
import { IFrame } from './IFrame';
import { NavInteractions } from '../utils/NavInteractions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const DIALOG_DEFAULT_USE = 'default';
const FULLSCREEN = 'fullscreen';
const SIDEBAR = 'sidebar';
const PANEL = 'panel';
const PRELOAD_CONTAINER_ROOT_ID = 'ifr-private-preload-root';
const POS_ADJUST_INTERVAL = 100;
const MAX_POS_ADJUSTMENTS = 4000 / POS_ADJUST_INTERVAL;
const OUT_OF_MODAL_POSITIONING = {
  height: '',
  width: '',
  top: '',
  left: '',
  position: 'static',
  'z-index': ''
};
const IN_MODAL_POSITIONING = {
  position: 'absolute',
  'z-index': 1112
};
const FULL_MODAL_EMBED_IN_MODAL_POSITIONING = {
  height: '100vh',
  width: '100%'
};
const ContainerPositionRelative = styled('div').withConfig({
  displayName: "ModalIFrame__ContainerPositionRelative"
})(["position:relative;width:100%;"]);
const toPx = v => `${v}px`;
const getOffset = el => {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return {
    top: rect.top + scrollTop,
    topToViewport: rect.top,
    left: rect.left + scrollLeft
  };
};
const getPreloadContainerRootEl = () => {
  let rootEl = document.getElementById(PRELOAD_CONTAINER_ROOT_ID);
  if (!rootEl) {
    rootEl = document.createElement('div');
    rootEl.id = PRELOAD_CONTAINER_ROOT_ID;
    document.body.appendChild(rootEl);
  }
  return rootEl;
};
const createPreloadContainerEl = () => {
  const el = document.createElement('div');
  getPreloadContainerRootEl().appendChild(el);
  return el;
};
const positionIFrameInModal = (iFrameContainerEl, modalEl, styles = {}) => {
  if (!modalEl) {
    return;
  }
  const offset = getOffset(modalEl);
  const positioning = Object.assign({}, IN_MODAL_POSITIONING, {
    top: toPx(offset.top),
    left: toPx(offset.left),
    'max-width': toPx(modalEl.offsetWidth)
  });
  const computedFrameContainerStyles = Object.assign({}, positioning, styles);
  const {
    height
  } = computedFrameContainerStyles;
  const {
    topToViewport
  } = offset;
  if (height && topToViewport > 0) {
    computedFrameContainerStyles.height = `calc(${height} - ${topToViewport}px)`;
  }
  Object.assign(iFrameContainerEl.style, computedFrameContainerStyles);
};
const resetIFramePosition = iFrameContainerEl => {
  Object.assign(iFrameContainerEl.style, OUT_OF_MODAL_POSITIONING);
};
const MODAL_DIALOG_PROPS = {
  centered: true,
  children: true,
  contextual: true,
  _dialogWidth: true,
  dialogClassName: true,
  onEsc: true,
  size: true,
  use: true,
  _use: true,
  width: true,
  _openCompleteRelay: true,
  _closeStartRelay: true
};
const MODAL_PROPS = Object.assign({}, MODAL_DIALOG_PROPS, {
  onCloseComplete: true,
  onCloseStart: true,
  onOpenComplete: true,
  onOpenStart: true,
  _contextual: true,
  rootNode: true
});
const PANEL_PROPS = {
  animate: true,
  align: true,
  children: true,
  containerZIndex: true,
  onCloseComplete: true,
  onCloseStart: true,
  onOpenComplete: true,
  onOpenStart: true,
  panelClassName: true,
  rootNode: true,
  scrollElementRef: true,
  width: true,
  _dontMakeNewLayer: true,
  panelKey: true
};
const MODAL_PANEL_PROPS = Object.assign({}, MODAL_DIALOG_PROPS, {
  centered: false,
  size: false,
  use: false,
  onCloseComplete: true,
  onCloseStart: true,
  onOpenComplete: true,
  onOpenStart: true,
  panelKey: true,
  _contextual: true,
  rootNode: true
});
class ModalIFrameComponent extends Component {
  static getDerivedStateFromProps(props, state) {
    let preloadedIFrame = state.preloadedIFrame;
    if (props.preload && !preloadedIFrame) {
      preloadedIFrame = {
        placeholderRef: /*#__PURE__*/createRef(),
        el: createPreloadContainerEl()
      };
    }
    return {
      actionHandlers: props.actionButtons.reduce((acc, action) => {
        acc[action.name] = evt => {
          // prevent clicks on buttons from propagating up to component rendering the modal
          evt.stopPropagation();
          state.handleAction(action.name);
        };
        return acc;
      }, {}),
      preloadedIFrame
    };
  }
  constructor() {
    super();
    this.getAppName = () => {
      var _window$hubspot;
      return ((_window$hubspot = window.hubspot) === null || _window$hubspot === void 0 || (_window$hubspot = _window$hubspot.bender) === null || _window$hubspot === void 0 ? void 0 : _window$hubspot.currentProject) || 'ui-modal-iframe';
    };
    this.handleAction = this.handleAction.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleEsc = this.handleEsc.bind(this);
    this.handleOnCloseComplete = this.handleOnCloseComplete.bind(this);
    this.handleOnMessage = this.handleOnMessage.bind(this);
    this.handleOnOpenComplete = this.handleOnOpenComplete.bind(this);
    this.handleOnOpenStart = this.handleOnOpenStart.bind(this);
    this.handleOnReady = this.handleOnReady.bind(this);
    this.positionIFrameInModal = this.positionIFrameInModal.bind(this);
    this.hostContext = null;
    this.adjustPosition = {
      interval: null,
      count: 0
    };
    this.state = {
      actionHandlers: null,
      handleAction: this.handleAction,
      open: false,
      preloadedIFrame: null,
      ready: false,
      previousNavVisibility: null
    };
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.preload && !this.props.preload) {
      this.resetIFramePosition();
      this.setState({
        ready: false
      });
    }
    if (this.props.preload) {
      if (this.props.show && !prevState.ready && this.state.ready) {
        this.positionIFrameInModal();
      }
      if (this.state.ready && (prevProps.height !== this.props.height || prevProps.width !== this.props.width)) {
        this.positionIFrameInModal();
      }
    }
  }
  componentWillUnmount() {
    if (this.props.preload) {
      this.resetIFramePosition();
      this.state.preloadedIFrame.el.parentElement.removeChild(this.state.preloadedIFrame.el);
    }
  }
  isUseFullscreen() {
    return this.props.use === FULLSCREEN;
  }
  isUseSidebar() {
    return this.props.use === SIDEBAR;
  }
  isUsePanel() {
    return this.props.use === PANEL;
  }
  isUseDialog() {
    return this.props.use === DIALOG_DEFAULT_USE;
  }
  isFullModalEmbed() {
    return !this.hasFooter();
  }
  isHeightNumeric() {
    return !`${this.props.height}`.endsWith('%') && !isNaN(parseInt(this.props.height, 10));
  }
  hasHeader() {
    return this.props.title;
  }
  hasFooter() {
    return this.props.actionButtons.length;
  }
  getIframePositioningStyles() {
    return this.isFullModalEmbed() ? FULL_MODAL_EMBED_IN_MODAL_POSITIONING : undefined;
  }
  positionIFrameInModal() {
    positionIFrameInModal(this.state.preloadedIFrame.el, this.state.preloadedIFrame.placeholderRef.current, this.getIframePositioningStyles());
  }
  stopAdjustingIFramePositioning() {
    clearInterval(this.adjustPosition.interval);
    this.adjustPosition.interval = null;
    this.adjustPosition.count = 0;
  }
  adjustIFramePositioning() {
    if (this.adjustPosition.interval) {
      return;
    }
    this.adjustPosition.interval = setInterval(() => {
      if (this.adjustPosition.count >= MAX_POS_ADJUSTMENTS || !this.props.preload || !this.props.show) {
        this.stopAdjustingIFramePositioning();
        return;
      }
      this.adjustPosition.count++;
      this.positionIFrameInModal();
    }, 100);
  }
  resetIFramePosition() {
    window.removeEventListener('resize', this.positionIFrameInModal);
    if (this.isUsePanel()) {
      window.removeEventListener('scroll', this.positionIFrameInModal);
    }
    this.stopAdjustingIFramePositioning();
    resetIFramePosition(this.state.preloadedIFrame.el);
  }
  performIFramePositioning() {
    if (!this.props.preload || !this.props.show) {
      return;
    }
    this.positionIFrameInModal();
    this.adjustIFramePositioning();
    window.addEventListener('resize', this.positionIFrameInModal);
    if (this.isUsePanel()) {
      window.addEventListener('scroll', this.positionIFrameInModal);
    }
  }
  handleOnReady(hostContext, data) {
    this.hostContext = hostContext;
    this.props.onReady(hostContext, data);
    this.setState({
      ready: true
    });
    if (!this.state.open) {
      return;
    }
    this.performIFramePositioning();
  }
  handleModalClosed(message) {
    const {
      onClose,
      preload
    } = this.props;
    if (!preload) {
      this.setState({
        ready: false
      });
    }
    onClose(message.payload);
  }
  handleOnMessage(message) {
    const {
      onMessage
    } = this.props;
    if (message.payload.type === MSG_TYPE_MODAL_DIALOG_CLOSE) {
      this.handleModalClosed(message);
    } else {
      onMessage(message);
    }
  }
  shouldChangeNavVisibility() {
    return this.getModalComponent() === Modal && this.isUseFullscreen();
  }
  handleOnCloseComplete() {
    const {
      onCloseComplete
    } = this.props;
    if (onCloseComplete) {
      onCloseComplete();
    }
    if (this.props.preload) {
      this.resetIFramePosition();
    }
    this.setState({
      open: false
    });
    if (this.shouldChangeNavVisibility()) {
      const navInteractions = NavInteractions.getInstance({
        appName: this.getAppName()
      });
      const {
        previousNavVisibility
      } = this.state;
      if (!previousNavVisibility) {
        // This should only happen in tests
        return;
      }
      const {
        isToolbarVisible: shouldShowToolbar,
        isVerticalNavVisible: shouldShowNav
      } = previousNavVisibility;
      navInteractions.useNavDisplayConfig({
        shouldShowNav,
        shouldShowToolbar
      });
    }
  }
  handleOnOpenStart() {
    const {
      onOpenStart
    } = this.props;
    if (onOpenStart) {
      onOpenStart();
    }
    if (this.shouldChangeNavVisibility()) {
      const navInteractions = NavInteractions.getInstance({
        appName: this.getAppName()
      });
      navInteractions.getCurrentVisibility().then(visibility => {
        this.setState({
          previousNavVisibility: visibility
        });
        navInteractions.useNavDisplayConfig(this.props.navDisplayConfig);
      }).catch(console.error);
    }
  }
  handleOnOpenComplete() {
    const {
      onOpenComplete
    } = this.props;
    if (onOpenComplete) {
      onOpenComplete();
    }
    this.setState({
      open: true
    });
    if (!this.state.ready) {
      return;
    }
    this.performIFramePositioning();
  }
  handleCloseModal(evt) {
    // prevent clicks on close button from propagating up to component rendering the modal
    evt.stopPropagation();
    this.handleAction(MSG_TYPE_MODAL_DIALOG_CLOSE);
  }
  handleAction(action) {
    if (!this.hostContext) {
      return;
    }
    this.hostContext.sendMessage(action, {});
  }
  handleEsc(evt) {
    const {
      onEsc
    } = this.props;
    if (onEsc) {
      onEsc();
    }
    this.handleCloseModal(evt);
  }
  getHandlers() {
    const {
      onEsc,
      title,
      use
    } = this.props;
    if (use === PANEL) {
      // UIPanel does not support an `onEsc` prop
      return {};
    }
    if (title) {
      return {
        onEsc
      };
    }
    return {
      onEsc: this.handleEsc
    };
  }
  getModalComponent() {
    switch (this.props.use) {
      case SIDEBAR:
        return ModalPanel;
      case PANEL:
        return Panel;
      default:
        return Modal;
    }
  }

  // Needed for `getModalComponentProps` filteringto work with the legacy propTypes from UIC
  getModalComponentLegacyProps() {
    switch (this.props.use) {
      case SIDEBAR:
        return MODAL_PANEL_PROPS;
      case PANEL:
        return PANEL_PROPS;
      default:
        return MODAL_PROPS;
    }
  }
  getModalComponentProps() {
    const ModalComponentLegacyProps = this.getModalComponentLegacyProps();
    return Object.keys(ModalComponentLegacyProps).reduce((acc, prop) => {
      if (!prop) {
        return acc;
      }
      if (this.props.hasOwnProperty(prop)) {
        acc[prop] = this.props[prop];
      }
      return acc;
    }, {});
  }
  getModalComponentWidth() {
    const width = parseInt(this.props.width, 10);
    if (isNaN(width)) {
      return this.props.width;
    }
    const {
      theme
    } = this.props;
    // Loose coupling with transitional Dialog
    const widthAdjustment = 2 * parseInt(theme.borderWidth['transitional-2'], 10);

    // Adjust for borders
    return this.isFullModalEmbed() ? width + widthAdjustment : width + 80;
  }
  getDialogBodyProps() {
    const showingProgressIndicator = this.shouldRender(this.props.showProgressIndicator);
    let props = {
      display: 'flex',
      position: 'relative'
    };
    if (this.isFullModalEmbed() && showingProgressIndicator) {
      props = Object.assign({}, props, {
        padding: '0 !important'
      });
      if (this.isUsePanel()) {
        props = Object.assign({}, props, {
          marginBlockStart: '0'
        });
      } else if (this.isUseSidebar()) {
        props = Object.assign({}, props, {
          marginBlockStart: '-600'
        });
      }
    }
    if (this.isFullModalEmbed() && !showingProgressIndicator) {
      props = Object.assign({}, props, {
        paddingInline: '0 !important'
      });
      if (!this.isUseSidebar() && !this.isUsePanel()) {
        props = Object.assign({}, props, {
          paddingBlockStart: '0 !important',
          paddingBlockEnd: '0 !important'
        });
      }
      if (this.isUsePanel()) {
        props = Object.assign({}, props, {
          marginBlockStart: '0'
        });
      } else if (this.isUseSidebar()) {
        props = Object.assign({}, props, {
          marginBlockStart: '-600'
        });
      }
    }
    return props;
  }
  getAdditionalIFrameProps() {
    const {
      theme
    } = this.props;
    let props = {
      padding: theme.space['0'],
      flexGrow: 1
    };
    if (this.isUseSidebar() || this.isUsePanel()) {
      props = Object.assign({}, props, {
        height: 'unset',
        width: '100%'
      });
    }
    return props;
  }
  getIFrameDimensions() {
    const fullyEmbedded = !(this.hasHeader() || this.hasFooter());
    let none;
    if (this.isUseFullscreen()) {
      none = !this.state.open || !fullyEmbedded && !this.props.show;
    } else {
      none = !this.props.show;
    }
    const {
      theme
    } = this.props;
    // Loose coupling with transitional Dialog
    const heightAdjustment = 4 * parseInt(theme.borderWidth['transitional-2'], 10);
    const height = this.isUseFullscreen() ? '100%' : this.isUseDialog() && this.isHeightNumeric() ? parseInt(this.props.height, 10) + heightAdjustment : this.props.height;
    const width = this.isUseFullscreen() ? '100%' : this.props.width;
    return {
      height: none ? 0 : height,
      width: none ? 0 : width
    };
  }
  shouldRender(indicator) {
    return !this.state.ready && indicator;
  }
  renderIFrame() {
    const _this$props = this.props,
      iframeProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const dimensions = this.getIFrameDimensions();
    const {
        'data-test-id': dataTestId,
        iframePassthruProps
      } = iframeProps,
      iframeRestProps = _objectWithoutPropertiesLoose(iframeProps, _excluded2);
    const enhancedIframePassthruProps = Object.assign({}, iframePassthruProps, this.getAdditionalIFrameProps());
    return /*#__PURE__*/_jsx(IFrame, Object.assign({
      iframePassthruProps: enhancedIframePassthruProps
    }, iframeRestProps, {
      "data-test-id": dataTestId,
      height: dimensions.height,
      onMessage: this.handleOnMessage,
      onReady: this.handleOnReady,
      width: dimensions.width
    }));
  }
  renderIFramePlaceholder() {
    const {
      height,
      width
    } = this.props;
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": PRELOAD_CONTAINER_ROOT_ID,
      ref: this.state.preloadedIFrame.placeholderRef,
      style: {
        height: `${height}px`,
        width: `${width}px`
      }
    });
  }
  renderProgressIndicator() {
    return /*#__PURE__*/_jsx(OptimisticNanoProgress, Object.assign({}, this.props.progressIndicatorProps));
  }
  renderLoadingIndicator() {
    return /*#__PURE__*/_jsx(LoadingOverlay, Object.assign({}, this.props.loadingIndicatorProps, {
      contextual: true
    }));
  }
  renderHeader() {
    if (!this.hasHeader()) {
      return null;
    }
    const {
      title,
      use
    } = this.props;
    const Header = use === PANEL ? PanelHeader : DialogHeader;
    return /*#__PURE__*/_jsxs(Header, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: this.handleCloseModal
      }), /*#__PURE__*/_jsx(H2, {
        children: title
      })]
    });
  }
  renderBody() {
    const {
      actionButtons: __ab,
      preload,
      showLoadingIndicator,
      showProgressIndicator,
      title: __t,
      use
    } = this.props;
    const bodyProps = this.getDialogBodyProps();
    const renderProgress = this.shouldRender(showProgressIndicator);
    const renderLoading = this.shouldRender(showLoadingIndicator);
    const Body = use === PANEL ? PanelBody : DialogBody;
    const content = /*#__PURE__*/_jsxs(Fragment, {
      children: [renderProgress && this.renderProgressIndicator(), /*#__PURE__*/_jsxs(ContainerPositionRelative, {
        children: [renderLoading && this.renderLoadingIndicator(), preload && this.renderIFramePlaceholder(), !preload && this.renderIFrame()]
      })]
    });
    if (use === PANEL) {
      if (this.isFullModalEmbed()) {
        return /*#__PURE__*/_jsx(Body, Object.assign({}, bodyProps, {
          children: content
        }));
      }
      return /*#__PURE__*/_jsx(Body, Object.assign({}, bodyProps, {
        children: /*#__PURE__*/_jsx(PanelSection, {
          display: "flex",
          children: content
        })
      }));
    }
    return /*#__PURE__*/_jsx(Body, Object.assign({}, bodyProps, {
      children: content
    }));
  }
  renderFooter() {
    if (!this.hasFooter()) {
      return null;
    }
    const {
      actionButtons,
      use
    } = this.props;
    const Footer = use === PANEL ? PanelFooter : DialogFooter;
    return /*#__PURE__*/_jsx(Footer, {
      children: actionButtons.map(({
        name,
        label,
        props = {}
      }) => /*#__PURE__*/_createElement(Button, Object.assign({}, props, {
        key: name,
        onClick: this.state.actionHandlers[name]
      }), label))
    });
  }
  renderPreloadedIFrame() {
    return /*#__PURE__*/createPortal(this.renderIFrame(), this.state.preloadedIFrame.el);
  }
  renderModal() {
    const {
      modalTestId,
      'data-test-id': dataTestId
    } = this.props;
    const ModalComponent = this.getModalComponent();
    const handlers = this.getHandlers();
    const width = this.getModalComponentWidth();
    const modalProps = this.getModalComponentProps();
    const testId = modalTestId || `${dataTestId}-modal`;
    return /*#__PURE__*/_jsxs(ModalComponent, Object.assign({}, modalProps, handlers, {
      onCloseComplete: this.handleOnCloseComplete,
      onOpenComplete: this.handleOnOpenComplete,
      onOpenStart: this.handleOnOpenStart,
      width: width,
      "data-test-id": testId,
      children: [this.renderHeader(), this.renderBody(), this.renderFooter()]
    }));
  }
  render() {
    const {
      preload,
      show
    } = this.props;
    if (!preload) {
      return show && this.renderModal();
    }
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [preload && this.renderPreloadedIFrame(), show && this.renderModal()]
    });
  }
}

// propTypes removed for React 19 compatibility — preserved as comments for future TS migration
// ModalIFrameComponent.propTypes = {
//   actionButtons: arrayOf(shape({ name: string.isRequired, label: string.isRequired, props: object })).isRequired,
//   loadingIndicatorProps: object.isRequired,
//   modalTestId: string,
//   onClose: func.isRequired,
//   preload: bool.isRequired,
//   progressIndicatorProps: object.isRequired,
//   show: bool.isRequired,
//   showLoadingIndicator: bool.isRequired,
//   showProgressIndicator: bool.isRequired,
//   title: string,
//   use: oneOf(['sidebar', 'panel', 'fullscreen', 'default']).isRequired,
//   'data-test-id': string,
//   navDisplayConfig: shape({ shouldShowNav: bool.isRequired, shouldShowToolbar: bool.isRequired }),
// };
ModalIFrameComponent.defaultProps = {
  centered: false,
  contextual: false,
  size: 'auto',
  allow: '',
  appInfo: {},
  appName: '',
  embeddedPassthruProps: {},
  group: 'iframeable',
  height: 200,
  iframePassthruProps: {},
  initTimeout: 8000,
  name: 'embed',
  onInitError: emptyFunction,
  onMessage: emptyFunction,
  onReadyError: emptyFunction,
  readyTimeout: 3000,
  scrolling: true,
  width: 200,
  _onTestContextReady: emptyFunction,
  actionButtons: [],
  loadingIndicatorProps: {},
  preload: false,
  progressIndicatorProps: {
    value: 5
  },
  show: true,
  showLoadingIndicator: false,
  showProgressIndicator: false,
  use: DIALOG_DEFAULT_USE
};
export const ModalIFrame = withTheme(ModalIFrameComponent);
export default ModalIFrame;
export const ModalIFrameWithRef = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(ModalIFrame, Object.assign({}, props, {
  innerRef: ref
})));